import { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import { KeyRound, Lock, CheckCircle2 } from "lucide-react";
import PasswordInput from "../components/PasswordInput";

/**
 * Player-facing reset-password page (Mod 2 user flow).
 * Reached via the link in the email/WhatsApp message (token in query string).
 */
export default function ResetPassword() {
    const location = useLocation();
    const navigate = useNavigate();
    const token = new URLSearchParams(location.search).get("token") || "";
    const [pw1, setPw1] = useState("");
    const [pw2, setPw2] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!token) { toast.error("Missing reset token. Use the link from your email."); return; }
        if (pw1.length < 6) { toast.error("Password must be at least 6 characters"); return; }
        if (pw1 !== pw2) { toast.error("Passwords do not match"); return; }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: pw1 });
            setDone(true);
            toast.success("Password reset successfully");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Reset failed");
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6"
            style={{ backgroundImage: "radial-gradient(ellipse at top, rgba(0,255,102,0.06), transparent 60%)" }}>
            <Toaster theme="dark" richColors position="top-center" />
            <div className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-lg p-8" data-testid="reset-card">
                <div className="flex flex-col items-center mb-6">
                    <div className="w-14 h-14 rounded-full bg-[#00FF66]/10 border border-[#00FF66]/40 flex items-center justify-center mb-3">
                        <KeyRound className="w-6 h-6 text-[#00FF66]" />
                    </div>
                    <h1 className="font-display text-2xl font-bold uppercase tracking-widest text-white">Reset Password</h1>
                </div>
                {done ? (
                    <div className="text-center" data-testid="reset-success">
                        <CheckCircle2 className="w-12 h-12 mx-auto text-[#00FF66] mb-3" />
                        <div className="text-white font-semibold">Password updated</div>
                        <div className="text-sm text-slate-400 mt-1">You can now sign in with your new password.</div>
                        <button onClick={() => navigate("/login")} data-testid="back-to-login-btn"
                            className="mt-6 w-full h-11 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded hover:bg-[#00cc52]">
                            Back to Sign In
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
                        {!token && (
                            <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded p-2" data-testid="missing-token-msg">
                                No reset token in URL. Please use the link from your email or WhatsApp.
                            </div>
                        )}
                        <div>
                            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">New Password</label>
                            <div className="mt-2">
                                <PasswordInput value={pw1} onChange={setPw1} testid="new-password-input" autoComplete="new-password" />
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Confirm Password</label>
                            <div className="mt-2">
                                <PasswordInput value={pw2} onChange={setPw2} testid="confirm-password-input" autoComplete="new-password" />
                            </div>
                        </div>
                        <button type="submit" disabled={loading || !token} data-testid="reset-submit"
                            className="w-full h-11 inline-flex items-center justify-center gap-2 bg-[#00FF66] text-black font-bold uppercase tracking-wide rounded hover:bg-[#00cc52] disabled:opacity-50 glow-green">
                            <Lock className="w-4 h-4" /> {loading ? "Resetting…" : "Reset Password"}
                        </button>
                        <p className="text-[10px] text-slate-600 text-center uppercase tracking-widest pt-2">
                            <Link to="/login" className="text-slate-400 hover:text-[#00FF66]">Back to sign-in</Link>
                        </p>
                    </form>
                )}
            </div>
        </div>
    );
}
